/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import { isWebUri } from "valid-url";
import DOMPurify from "dompurify";
import jsonpath from "jsonpath";
import { differenceInCalendarDays } from "date-fns";
import { NodeHtmlMarkdown } from "node-html-markdown";
import { REDACTED_STRING } from "./constants";
import { cleLogger } from "@prftesp/cle-logger";

const COMPONENT_NAME = "CCP Utility/helpers";

/**
 * checks if object is empty
 * @param {Object} obj object to test
 */
export const isEmptyObject = (obj) => !obj || Object.keys(obj).length === 0;

/**
 * Checks if a string meets AWS naming schema requirements
 * String contain at least 1 character, and consist of only letters and non-consecutive underscores
 * e.g.: PACE_Lex_Bot
 *
 * See the naming conventions required for the name field on the PutBot and PutIntent API's:
 *
 * https://docs.aws.amazon.com/lex/latest/dg/API_PutBot.html
 * https://docs.aws.amazon.com/lex/latest/dg/API_PutIntent.html
 *
 * @param {Object} name name string to test
 */
export const isValidLexBotResourceName = (name) => name.length < 51 && /^([A-Za-z]_?)+$/.test(name);

/**
 * Checks if the given ID is a valid GUID format.
 * @param {String} Id The Azure ID
 * @returns true if valid or false if not.
 */
export const isValidAzureId = (Id) =>
	/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(Id);

export const deepEqual = (object1, object2) => {
	const keys1 = Object.keys(object1);
	const keys2 = Object.keys(object2);
	if (keys1.length !== keys2.length) {
		return false;
	}
	for (const key of keys1) {
		const val1 = object1[key];
		const val2 = object2[key];
		const areObjects = isObject(val1) && isObject(val2);
		if ((areObjects && !deepEqual(val1, val2)) || (!areObjects && val1 !== val2)) {
			return false;
		}
	}
	return true;
};

const isObject = (object) => {
	return object != null && typeof object === "object";
};

/**
 * validates given string for special characters, maximum length
 * @param {String} a string to validate.
 * @param {maxLen} maximum allowed length for name
 * @returns message if invalid or false valid name.
 */
export const checkDashboardNameErrors = (name, maxLen) => {
	var regex = /^(?:[A-Za-z]+)(?:[A-Za-z0-9 _]*)$/;
	if (!name) {
		return "Dashboard name is required.";
	} else if (name.length > maxLen) {
		return `Report name cannot exceed more than ${maxLen} characters.`;
	} else if (!regex.test(name)) {
		return "Invalid dashboard name. Please provide a valid name. (Only alphanumeric characters, underscores, and spaces are allowed.)";
	} else {
		return false;
	}
};

/**
 * Checks if the given URL is valid d
 * @returns message if not valid false if valid URL.
 */
export const checkDashboardURIErrors = (url) => {
	if (!url) {
		return "Please provide a URL";
	} else if (!isWebUri(url)) {
		return "Please provide a valid URL / iFrame embed code.";
	} else {
		return false;
	}
};

/**
 * validates a given HTML snippet is Iframe code or not.
 * @param {HTML snippet} code
 * @returns true if valid iframe embed code else false.
 */
export const isValidIFrameCode = (code) => {
	const reg = /<iframe.*?>/;
	return reg.test(code);
};

export const extractSrcFromIframe = (content) => {
	try {
		var parser = new DOMParser();
		var parsedIframe = parser.parseFromString(content, "text/html");
		let iFrame = parsedIframe.getElementsByTagName("iframe");
		return iFrame[0].src;
	} catch (error) {
		return content;
	}
};

/**
 * Returns sanitized html
 * @param {string} html html string
 * @param {Object} options parsing options
 * @param {boolean} options.openLinksInNewTab adds target="_blank" to all anchor tags
 * @returns sanitized html
 */
export const sanitizeHtml = (html, options = {}) => {
	if (!html) {
		return "";
	}

	if (!isEmptyObject(options)) {
		DOMPurify.addHook("afterSanitizeAttributes", function (node) {
			// set all elements owning target to target=_blank
			if ("target" in node && options.openLinksInNewTab) {
				node.setAttribute("target", "_blank");
				node.setAttribute("rel", "noopener");
			}
		});
	}

	return DOMPurify.sanitize(html);
};

/**
 *  Converts an html string to markdown
 * @param {string} html html string
 * @returns markdown string
 */
export const htmlToMarkdown = (html) => {
	if (!html) {
		return "";
	}
	return NodeHtmlMarkdown.translate(html);
};

/**
 * returns the number of days from now
 * @param {Date | ISOString} targetDate
 */
export const getDaysSince = (targetDate) => {
	const diffDays = differenceInCalendarDays(new Date(targetDate), new Date());
	return diffDays;
};

// https://youmightnotneed.com/lodash#set
/**
 * Sets a given value inside an object using a string path
 * @param {Object} obj Object to set the value in
 * @param {string} path Path to set the value at
 * @param {*} value Value to be set at the given path
 */
const setAtPath = (obj, path, value) => {
	const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);

	pathArray.reduce((acc, key, i) => {
		if (acc[key] === undefined) {
			acc[key] = {};
		}

		if (i === pathArray.length - 1) {
			acc[key] = value;
		}

		return acc[key];
	}, obj);
};

/**
 * Returns a redacted version of the given value, keeping characters based on the provided field's options
 * @param {*} value Value to be redacted
 * @param {(string|PIIField)} field PII field used for redaction
 * @returns Redacted string
 */
export const redactValue = (value, field) => {
	try {
		// redact entire array or object values
		if (Array.isArray(value) || typeof value === "object") {
			return REDACTED_STRING;
		}

		let { keepFirst, keepLast } = typeof field === "object" ? field : {};
		const strVal = String(value);

		// value length is short, return redacted string to hide original length
		if (!keepFirst && !keepLast && strVal.length < REDACTED_STRING.length) {
			return REDACTED_STRING;
		}

		if (!keepFirst || keepFirst < 0) {
			keepFirst = 0;
		}
		if (!keepLast || keepLast < 0) {
			keepLast = 0;
		}

		// trying to redact too many characters, original value will be visible
		const starCount = strVal.length - keepFirst - keepLast;
		if (starCount < 1) {
			cleLogger.warn("Redacted field is requesting to keep more characters than the value length.", {
				additionalData: { field },
				componentName: COMPONENT_NAME,
			});

			return REDACTED_STRING;
		}

		return (
			strVal.substring(0, keepFirst) + // start section
			"*".repeat(starCount > 0 ? starCount : 0) + // redacted section
			strVal.substring(strVal.length - keepLast || strVal.length) // end section
		);
	} catch (error) {
		cleLogger.warn("Error when redacting value", {
			additionalData: { error: cleLogger.errorToObject(error), field },
			componentName: COMPONENT_NAME,
		});

		return REDACTED_STRING;
	}
};

/**
 * Redacts PII from a given object or array using the provided fields
 * @param {(Object|Array)} obj Object or array to redact PII from
 * @param {(string|PIIField)[]} fields Fields specifying where PII exists in given object
 * @returns Provided object or array with the specified fields redacted
 */
export const redactPII = (obj, fields) => {
	try {
		// make sure an obj and an array of fields were passed in
		if (!obj || !fields || fields.length < 1) {
			cleLogger.warn("Redact PII function was called without object or fields", {
				additionalData: { fields },
				componentName: COMPONENT_NAME,
			});

			return REDACTED_STRING;
		}

		// start redacting fields that were provided
		const newObj = JSON.parse(JSON.stringify(obj));
		fields.forEach((field) => {
			// the fields can either be a string path,
			// or an object containing path, keepFirst, and keepLast values
			const path = typeof field === "string" ? field : field.path;

			// find the nodes within the object using the path
			const nodes = jsonpath.nodes(newObj, path);

			// start redacting each node found
			nodes.forEach((node) => {
				let { path: setPath } = node;
				const { value } = node;

				// make sure a value existed at that path before redacting
				if (Array.isArray(setPath) && setPath[0] === "$" && value !== undefined) {
					setPath = setPath.slice(1); // remove "$"
					setAtPath(newObj, setPath, redactValue(value, field));
				}
			});
		});

		return newObj;
	} catch (error) {
		cleLogger.warn("Error when redacting PII from object", {
			additionalData: { error: cleLogger.errorToObject(error), fields },
			componentName: COMPONENT_NAME,
		});

		return REDACTED_STRING;
	}
};

/**
 * Checks whether the total file size of an array of files exceeds given limit
 * @param {File[]} filesArr array of file objects
 * @param {Number} limit max limit to check against
 * @returns {boolean} true if total size of files exceeds limit
 */
export const totalFilesSizeExceedLimit = (filesArr, limit) => {
	if (!filesArr || filesArr.length < 1 || !limit) {
		return false;
	}

	const totalSize = filesArr.reduce((prev, curr) => prev + curr.size, 0);
	const totalSizeMb = totalSize / (1024 * 1024);
	return totalSizeMb > limit;
};
